import { AloftTime } from '@aloft/toolkit-shared';
import { captureException } from '@aloft/toolkit-shared/src/sentry/frontend.ts';
import { pdf } from '@react-pdf/renderer';

import { useState } from 'react';
import { UseFormReturn } from 'react-hook-form';

import {
  fetchCompleteTkv2OrderAPI,
  fetchPostReportAPI,
  fetchPostReportSignatureAPI,
  fetchPutTkv2CommentaryAttachmentAPI,
} from '../../../hooks/api/toolkitComponents.ts';
import { useToolkitContext } from '../../../hooks/api/toolkitContext.ts';
import { uploadBlob } from '../../../hooks/api/uploadBlob.ts';
import { Tkv2PDFModule } from '../../../modules/tkv2PDF/Tkv2PDFModule.tsx';
import { AppraisifyReport } from '../../../modules/tkv2PDF/types.ts';
import { generateTkv2PDFChartImages } from '../../../modules/tkv2PDF/utils/generateTkv2PDFChartImages.ts';
import { setAccountSignatureBlobId } from '../../../redux/features/accountSlice.ts';
import { selectCommentary } from '../../../redux/features/selectors/commentarySelectors/selectCommentary.ts';
import {
  selectIncludeCostApproach,
  selectIsMockOrder,
} from '../../../redux/features/selectors/orderSelectors.ts';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks.ts';
import { CertifyModalFormOutput } from '../schemas.ts';

export const useCertifyModalSubmission = ({
  orderID,
  reportData,
}: {
  orderID: string;
  reportData: AppraisifyReport;
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [isError, setIsError] = useState(false);
  const [submissionText, setSubmissionText] = useState('');

  const context = useToolkitContext();
  const { fetcherOptions } = context;
  const dispatch = useAppDispatch();
  const commentary = useAppSelector((state) => selectCommentary(state));
  const isMock = useAppSelector(selectIsMockOrder);
  const includeCostApproach = useAppSelector(selectIncludeCostApproach);

  const { pdfData: tkv2PDFData, appraisalData } = reportData;
  const submitModal = async (form: UseFormReturn<CertifyModalFormOutput>) => {
    setSubmissionText('Signing the report...');
    setIsSubmitting(true);

    try {
      const formValues = form.getValues();
      const { signatureFile } = formValues;
      const { stateSealFile } = formValues;

      let signatureImages: {
        signatureImage?: string;
        stateSealImage?: string;
      } = {
        signatureImage: undefined,
        stateSealImage: undefined,
      };

      if (!isMock) {
        if (!signatureFile) {
          setIsSubmitting(false);
          return;
        }

        const signatureBlob = await uploadBlob(signatureFile, 'report-signature', context);
        let stateSealBlob;

        if (stateSealFile) {
          stateSealBlob = await uploadBlob(stateSealFile, 'report-state-seal', context);
        }

        await fetchPostReportSignatureAPI({
          ...fetcherOptions,
          pathParams: { orderID },
          body: {
            signatureBlobId: signatureBlob.id,
            stateSealBlobId: stateSealBlob?.id,
            storeSignatureBlobId: formValues.saveSignature,
          },
        });

        if (formValues.saveSignature) {
          dispatch(setAccountSignatureBlobId(signatureBlob.id));
        }

        signatureImages = {
          signatureImage: URL.createObjectURL(signatureFile),
          stateSealImage: stateSealFile ? URL.createObjectURL(stateSealFile) : undefined,
        };
      }

      await fetchPutTkv2CommentaryAttachmentAPI({
        ...fetcherOptions,
        pathParams: { orderID },
        body: {
          ...commentary,
          reconciliation: {
            ...commentary.reconciliation,
            certificationCommentary: formValues.certificationCommentary,
          },
        },
      });

      setSubmissionText('Creating a copy of the report...');

      const chartImages = await generateTkv2PDFChartImages(
        tkv2PDFData.chartParams,
        includeCostApproach,
        tkv2PDFData.marketAggregateData,
      );

      const signedAt = AloftTime.now().toAloftDate().format('MM/dd/yyyy');

      const reportBlob = await pdf(
        <Tkv2PDFModule
          {...tkv2PDFData}
          tkv2ChartImages={chartImages}
          signedAt={signedAt}
          signatureImages={signatureImages}
        />,
      ).toBlob();

      const uploadedReportBlob = await uploadBlob(reportBlob, 'report-pdf', context);

      await fetchPostReportAPI({
        ...fetcherOptions,
        pathParams: { orderID },
        body: {
          reportBlobId: uploadedReportBlob.id,
        },
      });

      setSubmissionText('Delivering the report...');

      await fetchCompleteTkv2OrderAPI({
        ...fetcherOptions,
        pathParams: { orderID },
        body: {
          appraisalData,
        },
      });
      setIsCompleted(true);
      setIsSubmitting(false);
    } catch (error) {
      setIsError(true);
      setIsSubmitting(false);

      captureException(error);
    }
  };

  return {
    submitModal,
    isSubmitting,
    isError,
    isCompleted,
    submissionText,
  };
};
